import { ImagePlusIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
  BlockSeparator,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasInput,
  CanvasPercentageInput,
  CanvasSimpleSelect,
  PercentOperatorSelect,
} from "@/components/canvas-editor/components/Inputs";
import {
  modelProbabilityExplanation,
  NodeDocs,
} from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  ImageCategorizationShape,
  imageCategorizationType,
  comparisonOperators,
  ComparisonOperator,
  ImageCategorizationProps,
  metadataType,
  Dimensions,
  standardCategories,
} from "@/components/canvas-editor/shapes/shapes";
import {
  MIN_H,
  ToolStateNode,
  useIsReadonlyEditor,
} from "@/components/canvas-editor/utils";
import { Label } from "@/components/ui/label";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export function isImageCategorizationShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is ImageCategorizationShape {
  return shape.type === imageCategorizationType;
}

export class ImageCategorizationTool extends ToolStateNode {
  static override id = imageCategorizationType;

  static override uiToolDef = (editor: Editor) => ({
    id: imageCategorizationType,
    icon: ImagePlusIcon,
    label: ImageCategorizationShapeUtil.docs.title,
    defaultColor: ImageCategorizationShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(imageCategorizationType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={ImageCategorization}
        util={ImageCategorizationShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: imageCategorizationType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class ImageCategorizationShapeUtil extends BindableUtil<ImageCategorizationShape> {
  static override migrations = createMigrations(imageCategorizationType);
  static override type = imageCategorizationType;

  static override docs = {
    defaultColor: "pink" as const,
    title: "Image Categorization",
    description:
      "Use AI to analyze if images match any arbitrary category or concept. Enter any label and the model will assess how well the image matches that concept.",
  };

  override getType() {
    return imageCategorizationType;
  }

  static override props: RecordProps<ImageCategorizationShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    h: T.number,
    isReadOnly: T.boolean.optional(),
    tag: T.string,
    operator: T.literalEnum(...comparisonOperators),
    value: T.or(T.number, T.string),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 360,
      minW: 400,
      h: MIN_H,
      tag: "",
      operator: ">=" as const,
      value: 0.5,
      metadata: undefined,
    };
  }

  override component(shape: ImageCategorizationShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <ImageCategorization
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function ImageCategorization({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: ImageCategorizationShape;
  className?: string;
  updateShape: (
    shape: ImageCategorizationShape,
    props: Partial<ImageCategorizationProps>,
  ) => void;
}) {
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );
  const isReadOnly = useIsReadonlyEditor();
  const isUsingDeprecatedTag = !standardCategories.includes(shape.props.tag);

  if (isUsingDeprecatedTag) {
    return (
      <BlockContainerShapeContext value={shape}>
        <BlockContainer
          resultState={shape.props.resultState}
          pattern={shape.props.metadata?.pattern}
          isBound={isBound}
          className={cn(className, "animate-pulse")}
          variant={
            shape.props.metadata?.color ||
            ImageCategorizationShapeUtil.docs.defaultColor
          }
          isSelected={isSelected}
          onUpdateDimensions={onUpdateDimensions}
          dynamicHeight={true}
        >
          <NodeBlockHeader
            props={shape.props}
            docs={ImageCategorizationShapeUtil.docs}
            isDeprecated={true}
          />
          <BlockBody>
            <div className="flex flex-col gap-2 flex-1">
              <div className="flex gap-2">
                <SimpleTooltip message="Enter any category or concept to analyze in images">
                  <CanvasInput
                    aria-label="Category"
                    value={shape.props.tag}
                    type="text"
                    className="w-[160px] shrink-0"
                    placeholder="Enter category..."
                    onValueChange={(v) => updateShape(shape, { tag: v })}
                  />
                </SimpleTooltip>
                <PercentOperatorSelect
                  value={shape.props.operator}
                  onValueChange={(v) =>
                    updateShape(shape, { operator: v as ComparisonOperator })
                  }
                />
                <SimpleTooltip message={modelProbabilityExplanation}>
                  <CanvasPercentageInput
                    aria-label="Threshold"
                    value={shape.props.value as number}
                    className="flex-1"
                    placeholder="Threshold"
                    onValueChange={(value) => updateShape(shape, { value })}
                  />
                </SimpleTooltip>
              </div>
              {!isReadOnly && (
                <>
                  <BlockSeparator />
                  <div className="flex flex-col gap-2 py-2">
                    <Label>Please choose a standard category</Label>
                    <CanvasSimpleSelect
                      aria-label="Standard Category"
                      options={standardCategories}
                      placeholder="Choose a standard category"
                      onValueChange={(v) => updateShape(shape, { tag: v })}
                    />
                  </div>
                </>
              )}
            </div>
          </BlockBody>
        </BlockContainer>
      </BlockContainerShapeContext>
    );
  }

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        variant={
          shape.props.metadata?.color ||
          ImageCategorizationShapeUtil.docs.defaultColor
        }
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={ImageCategorizationShapeUtil.docs}
        />
        <BlockBody>
          <SimpleTooltip message="Enter any category or concept to analyze in images">
            <CanvasSimpleSelect
              aria-label="Category"
              options={standardCategories}
              placeholder="Choose a standard category"
              className="w-[160px] shrink-0"
              value={shape.props.tag}
              onValueChange={(v) => updateShape(shape, { tag: v })}
            />
          </SimpleTooltip>
          <PercentOperatorSelect
            value={shape.props.operator}
            onValueChange={(v) =>
              updateShape(shape, { operator: v as ComparisonOperator })
            }
          />
          <SimpleTooltip message={modelProbabilityExplanation}>
            <CanvasPercentageInput
              aria-label="Threshold"
              value={shape.props.value as number}
              className="flex-1"
              placeholder="Threshold"
              onValueChange={(value) => updateShape(shape, { value })}
            />
          </SimpleTooltip>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
